<script setup lang="ts">
  import { ref, reactive, computed, onMounted, onUnmounted, watch } from "vue";
  import { Button, IconArrowRight, IconRoundedSuccess, Text } from "@ui-kit";
  import { useBarcode, useReadRfid } from "@plugins";
  import { Image, SearchItemUHFDialog, OrderTypeIcon } from "@src/components";
  import {
    type Product,
    ResponseCode,
    useGetWTFIR,
    WTFIRInputType,
    useOrderActionDone,
    useOrderStatus,
    itemGetZoneApi,
  } from "@src/api";
  import { useProfileOptions } from "@src/hooks";
  import { useStateActionList, useOrderItemListState, useGoodZoneState } from "./state";
  import { useOrderListStore, useShopZoneListStore, useWidgetsStore } from "@src/store";
  import { formatZoneHierarchy } from "@src/utils";
  import { default as OrderCardCompleteDialog } from "./OrderCardCompleteDialog.vue";
  import { default as OrderCardRouteDirection } from "./OrderCardRouteDirection.vue";
  import { default as OrderCardComment } from "./OrderCardComment.vue";
  import { default as OrderCardCollectedProgress } from "./OrderCardCollectedProgress.vue";
  import { default as OrderCardItemPicking } from "./OrderCardItemPicking.vue";

  const emit = defineEmits<{ (e: "complete"): void }>();

  const isCompleteModalOpen = ref(false);
  const activeIndex = ref(0);
  const itemListRef = ref<HTMLElement | null>(null);
  const itemListWrapperRef = ref<HTMLElement | null>(null);
  const isDragging = ref(false);
  const startX = ref(0);
  const currentTranslate = ref(0);
  const dragTranslate = ref(0);
  const animationId = ref<number | null>(null);
  const state_search_uhf_dialog = reactive<{ visible: boolean; item: Product | null }>({ visible: false, item: null });

  const { execute: getWTFIR } = useGetWTFIR();
  const { mutateAsync: orderActionDone } = useOrderActionDone();
  const { mutateAsync: orderStatusDone, isPending: isOrderStatusPending } = useOrderStatus();

  const barcode = useBarcode();
  const readRfid = useReadRfid();
  const shopZoneListStore = useShopZoneListStore();
  const orderListStore = useOrderListStore();
  const widgets_store = useWidgetsStore();
  const actionListState = useStateActionList();
  const orderItemListState = useOrderItemListState();
  const goodZoneState = useGoodZoneState();
  const { profile, hasPermission } = useProfileOptions();

  const handleCompleteOrder = async () => {
    if (!widgets_store.order_card.order_id) return;

    const actionsCountCancelled = actionListState.getCountActions({ status: "request" });

    if (actionsCountCancelled > 0) {
      isCompleteModalOpen.value = true;
    } else {
      const result = await orderStatusDone({
        order_id: widgets_store.order_card.order_id,
        status: "done",
      });

      if (result.code === ResponseCode.OK) {
        emit("complete");
      }
    }
  };

  const order = computed(() => orderListStore.map[widgets_store.order_card.order_id]);
  const countGoods = computed(() => actionListState.getCountActions());
  const countCollectedGoods = computed(() => actionListState.getCountActions({ status: "done" }));
  const itemsList = computed(() => orderItemListState.value.list);
  const itemsCount = computed(() => itemsList.value.length);

  // Логика для точек (максимум 5 видимых, все точки отрисованы)
  const maxDots = 5;
  const dotSize = 6; // размер точки
  const dotGap = 8; // отступ между точками
  const dotWidth = dotSize + dotGap; // общая ширина точки с отступом

  // Вычисление смещения для контейнера точек
  const dotTranslateX = computed(() => {
    if (itemsCount.value <= maxDots) return 0;

    const centerPosition = Math.floor(maxDots / 2); // 2

    // До достижения индекса 3 (0, 1, 2) смещение не нужно - видим точки 0-4
    if (activeIndex.value <= centerPosition) return 0;

    // Вычисляем смещение так, чтобы активная точка была в центре видимой области
    // При activeIndex=3 видим точки 1-5, при activeIndex=4 видим 2-6 и т.д.

    // Смещаем так, чтобы активная точка была в центре (позиция 2 из 5 видимых)
    let targetStartIndex = activeIndex.value - centerPosition;

    // Но не можем начать позже, чем (itemsCount - maxDots)
    // Это нужно чтобы последняя точка была видна
    const maxStartIndex = itemsCount.value - maxDots;
    targetStartIndex = Math.min(targetStartIndex, maxStartIndex);

    // Вычисляем смещение для этого startIndex
    const translate = targetStartIndex * dotWidth;

    return -translate;
  });

  const checkIsCompleted = (item: (typeof itemsList.value)[number]) => {
    return item.goods.every(
      (good) =>
        actionListState.getCountActions({ itemId: good.item.item_id }) > 0 &&
        actionListState.getCountActions({ itemId: good.item.item_id }) ===
          actionListState.getCountActions({ itemId: good.item.item_id, status: "done" }),
    );
  };

  // Переключение на индекс
  const goToIndex = (index: number) => {
    if (index < 0 || index >= itemsCount.value) return;

    activeIndex.value = index;
    updateCarouselPosition();
  };

  // Обновление позиции карусели
  const updateCarouselPosition = () => {
    if (!itemListRef.value || !itemListWrapperRef.value) return;

    const containerWidth = itemListWrapperRef.value.offsetWidth;
    const translateX = -activeIndex.value * containerWidth;

    currentTranslate.value = translateX;
    itemListRef.value.style.transition = "transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1)";
    itemListRef.value.style.transform = `translateX(${translateX}px)`;
  };

  // Вычисление масштаба для элемента
  const getItemScale = (index: number) => {
    if (!itemListRef.value || !itemListWrapperRef.value) return 1;

    const containerWidth = itemListWrapperRef.value.offsetWidth;
    const currentPosition = isDragging.value ? dragTranslate.value : currentTranslate.value;

    // Позиция центра элемента относительно центра контейнера
    // Элемент начинается на позиции index * containerWidth, с учетом translateX
    const elementLeftEdge = index * containerWidth + currentPosition;
    const elementCenter = elementLeftEdge + containerWidth / 2;
    const containerCenter = containerWidth / 2;
    const distanceFromCenter = Math.abs(elementCenter - containerCenter);

    // Максимальное расстояние (половина ширины контейнера) = масштаб 0.8
    const maxDistance = containerWidth / 2;
    const normalizedDistance = Math.min(distanceFromCenter / maxDistance, 1);

    // Масштаб от 1 (в центре) до 0.8 (на краю)
    const scale = 1 - normalizedDistance * 0.2;
    return Math.max(scale, 0.8);
  };

  // Touch/Swipe обработчики
  const handleTouchStart = (e: TouchEvent) => {
    isDragging.value = true;
    startX.value = e.touches[0].clientX;

    if (animationId.value) cancelAnimationFrame(animationId.value);
    if (itemListRef.value) itemListRef.value.style.transition = "none";
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging.value || !itemListRef.value) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - startX.value;
    const translateX = currentTranslate.value + diff;

    dragTranslate.value = translateX;
    itemListRef.value.style.transform = `translateX(${translateX}px)`;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!isDragging.value || !itemListWrapperRef.value) return;

    const containerWidth = itemListWrapperRef.value.offsetWidth;
    const currentX = e.changedTouches[0].clientX;
    const diff = startX.value - currentX;
    const threshold = containerWidth * 0.2; // 20% от ширины для переключения

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && activeIndex.value < itemsCount.value - 1) {
        goToIndex(activeIndex.value + 1);
      } else if (diff < 0 && activeIndex.value > 0) {
        goToIndex(activeIndex.value - 1);
      } else {
        updateCarouselPosition();
      }
    } else {
      updateCarouselPosition();
    }

    isDragging.value = false;
    dragTranslate.value = 0;
  };

  const handleMouseDown = (e: MouseEvent) => {
    isDragging.value = true;
    startX.value = e.clientX;

    if (animationId.value) cancelAnimationFrame(animationId.value);
    if (itemListRef.value) itemListRef.value.style.transition = "none";
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.value || !itemListRef.value) return;

    const currentX = e.clientX;
    const diff = currentX - startX.value;
    const translateX = currentTranslate.value + diff;

    dragTranslate.value = translateX;
    itemListRef.value.style.transform = `translateX(${translateX}px)`;
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (!isDragging.value || !itemListWrapperRef.value) return;

    const containerWidth = itemListWrapperRef.value.offsetWidth;
    const diff = startX.value - e.clientX;
    const threshold = containerWidth * 0.2;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && activeIndex.value < itemsCount.value - 1) {
        goToIndex(activeIndex.value + 1);
      } else if (diff < 0 && activeIndex.value > 0) {
        goToIndex(activeIndex.value - 1);
      } else {
        updateCarouselPosition();
      }
    } else {
      updateCarouselPosition();
    }

    isDragging.value = false;
    dragTranslate.value = 0;
  };

  const scanData = async (inputScanData: string, inputScanType: WTFIRInputType) => {
    const resultWTFIR = await getWTFIR({ data: inputScanData, input_type: inputScanType });

    const item_id = resultWTFIR.data.value.items[0];
    const action = actionListState.getByItemId(item_id, "collect");

    if (action) {
      const resultOrderActionDone = await orderActionDone({ action_id: action.action_id });

      if (resultOrderActionDone.code === ResponseCode.OK) {
        actionListState.update(resultOrderActionDone.action);

        const index = itemsList.value.findIndex((item) => item.goods.some((good) => good.item.item_id === item_id));

        if (index !== -1) {
          activeIndex.value = index;
          updateCarouselPosition();
        }
      }
    }
  };

  const getZoneTitle = (item_id: string) => {
    const zone_ids = goodZoneState.get(item_id);

    if (!zone_ids) return "";

    const zones = zone_ids.map((zone_id) => shopZoneListStore.map[zone_id]);

    if (zones.length > 0) {
      const formattedZones = formatZoneHierarchy(zones[0].hierarchy, (id) => shopZoneListStore.map[id]?.title);
      return formattedZones.length > 2
        ? [formattedZones.slice(0, 2).join(" "), formattedZones.slice(2).join(" | ")].join(" | ")
        : formattedZones.join(" | ");
    }

    return "";
  };

  const handleOpenSearchUHFDialog = (item: Product) => {
    state_search_uhf_dialog.item = item;
    state_search_uhf_dialog.visible = true;
  };

  const handleCloseSearchUHFDialog = () => {
    state_search_uhf_dialog.visible = false;
    state_search_uhf_dialog.item = null;
  };

  const handleClickSize = (item: Product) => {
    state_search_uhf_dialog.visible = false;
    state_search_uhf_dialog.item = null;
    widgets_store.open("search_uhf", { item, index: 2 });
  };

  // Инициализация карусели
  watch(
    () => itemsList.value.length,
    () => {
      if (itemsList.value.length > 0) {
        activeIndex.value = 0;
        updateCarouselPosition();
      }
    },
    { immediate: true },
  );

  onMounted(async () => {
    barcode.subscribe((data) => {
      scanData(data, WTFIRInputType.barcode);
    });
    readRfid.subscribe((data) => {
      scanData(data.value, WTFIRInputType.rfid);
    });

    if (itemListRef.value) {
      itemListRef.value.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    if (order.value.place_from === "shopfloor") {
      const item_ids = itemsList.value.map((item) => item.seria.item_id);
      const result = await itemGetZoneApi({ item_id: item_ids });

      if (result.code == ResponseCode.OK) {
        goodZoneState.set(result.links);
      }
    }

    // Обновляем позицию после монтирования
    setTimeout(updateCarouselPosition, 0);
  });

  onUnmounted(() => {
    barcode.unsubscribe();
    readRfid.unsubscribe();

    if (animationId.value) cancelAnimationFrame(animationId.value);
    if (itemListRef.value) itemListRef.value.removeEventListener("mousedown", handleMouseDown);

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  });
</script>

<template>
  <div class="order-card-picking" data-testid="order-card-picking">
    <div class="content">
      <div class="info">
        <div class="info-container">
          <div class="number-route">
            <Text font="heading-H5">{{ `№${order.order_number}` }}</Text>
            <OrderCardRouteDirection
              :order_place_from="order.place_from"
              :store="order.place_from_type"
              :order_place_section="order.place_section"
              :order_place_to="order.place_to"
            />
          </div>
          <OrderTypeIcon :type="order.order_type" :size="36" />
        </div>
        <OrderCardComment v-if="order.comment" :comment="order.comment" />
        <OrderCardCollectedProgress :countGoods="countGoods" :countCollectedGoods="countCollectedGoods" />
      </div>
      <div ref="itemListWrapperRef" class="item-list-wrapper">
        <div
          ref="itemListRef"
          class="item-list"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div
            v-for="(item, index) in itemsList"
            :key="item.seriaId"
            class="item-wrapper"
            :style="{ transform: `scale(${getItemScale(index)})` }"
          >
            <OrderCardItemPicking
              :zone="getZoneTitle(item.seria.item_id)"
              :img_url="item.seria.image_url"
              :name="item.seria.title"
              :article="item.seria.article"
              :color="item.seria.color"
              :goods="item.goods"
              :isCompleted="checkIsCompleted(item)"
              @click:locator="() => handleOpenSearchUHFDialog(item.seria)"
            />
          </div>
        </div>
        <div class="dot-list">
          <div class="dot-container-wrapper" :style="itemsList.length < 5 ? { justifyContent: 'center' } : {}">
            <div ref="dotContainerRef" class="dot-container" :style="{ transform: `translateX(${dotTranslateX}px)` }">
              <span
                v-for="(item, index) in itemsList"
                :key="item.seriaId"
                class="dot"
                :class="{ active: index === activeIndex }"
              />
            </div>
          </div>
        </div>
        <div class="image-list">
          <div class="image-list-scroll">
            <div
              v-for="(item, index) in itemsList"
              :key="item.seriaId"
              class="image-list-scroll-item"
              @click="goToIndex(index)"
            >
              <div v-if="checkIsCompleted(item)" class="success-banner">
                <IconRoundedSuccess style="width: 32px; height: 23px" />
              </div>
              <Image :url="item.seria.image_url" :class="{ active: index === activeIndex }" />
            </div>
          </div>
          <div class="image-list-shadow">
            <Button
              v-if="profile?.current_user.user_id === order.executed_by && hasPermission('mobile.order_status')"
              asSquare
              :is_loading="isOrderStatusPending"
              size="l"
              @click="handleCompleteOrder"
              data-testid="order-complete-btn"
              class="image-list-button"
            >
              <template #iconBefore>
                <IconArrowRight style="stroke: var(--sf-base-icon-white)" />
              </template>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <OrderCardCompleteDialog
    :isOpen="isCompleteModalOpen"
    @close="isCompleteModalOpen = false"
    @complete="emit('complete')"
  />
  <SearchItemUHFDialog
    :is_open="state_search_uhf_dialog.visible"
    :seria_guid="
      state_search_uhf_dialog.item?.is_virtual
        ? state_search_uhf_dialog.item?.external_id
        : state_search_uhf_dialog.item?.seria_guid
    "
    :img_url="state_search_uhf_dialog.item?.image_url"
    :article="state_search_uhf_dialog.item?.article"
    :name="state_search_uhf_dialog.item?.title"
    :color="state_search_uhf_dialog.item?.color"
    @click:close="handleCloseSearchUHFDialog"
    @click:size="handleClickSize"
  />
</template>

<style scoped>
  .order-card-picking {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    overflow-y: hidden;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      background-color: var(--sf-base-background-primary);
      overflow-y: auto;
      gap: 8px;
    }

    .info {
      display: flex;
      flex-direction: column;
      padding: 0 16px 16px;
      gap: 8px;
      background-color: var(--sf-base-background-white);
      border-radius: 0 0 16px 16px;
    }

    .info-container {
      display: flex;
      justify-content: space-between;
    }

    .number-route {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .item-list-wrapper {
      overflow: hidden;
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 8px;
      padding-bottom: 16px;
      background-color: var(--sf-base-background-white);
      border-radius: 16px 16px 0 0;

      /* Отключаем выделение текста при перетаскивании */
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }

    .item-list {
      display: flex;
      flex: 1;
      transition: transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1);
      will-change: transform;

      .item-wrapper {
        display: flex;
        flex: 0 0 100%;
        transition: transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1);
        transform-origin: center;
        will-change: transform;
      }
    }

    .image-list-shadow {
      display: flex;
      justify-content: flex-end;
      position: absolute;
      right: 16px;
      top: 0;
      width: 80px;
      height: 100%;
      background: linear-gradient(
        to left,
        var(--sf-base-background-white),
        var(--sf-base-background-white),
        var(--sf-base-background-white),
        transparent
      );
      pointer-events: none;

      & :deep(.button) {
        pointer-events: all;
      }
    }

    .dot-list {
      display: flex;
      justify-content: center;
      width: 100%;
    }

    .dot-container-wrapper {
      overflow: hidden;
      width: calc(6px * 5 + 8px * 4); /* 5 точек по 6px + 4 gap по 8px = 62px */
      display: flex;
      justify-content: flex-start;
    }

    .dot-container {
      display: flex;
      gap: 8px;
      transition: transform 0.4s ease;

      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: var(--sf-base-border-dark);
        flex-shrink: 0;
        transition:
          opacity 0.4s ease,
          transform 0.4s ease;
        opacity: 0.4;

        &.active {
          opacity: 1;
        }
      }
    }

    .image-list {
      display: flex;
      align-items: center;
      padding: 0 16px;
      position: relative;

      .image-list-scroll {
        flex: 1;
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
        position: relative;
        display: flex;
        gap: 8px;
        padding-right: 70px;

        &::-webkit-scrollbar {
          display: none;
        }

        & :deep(.image) {
          outline: 1px solid var(--sf-base-border-primary);
          outline-offset: -1px;
          cursor: pointer;
          flex-shrink: 0;
          transition: border-color 0.4s ease;

          &.active {
            outline-offset: -2px;
            outline: 2px solid var(--sf-base-border-black);
          }
        }

        & :deep(.image img) {
          height: 52px;
          width: 44px;
          object-fit: cover;
        }
      }

      .image-list-scroll-item {
        position: relative;

        .success-banner {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--sf-base-background-loader);
        }
      }

      .image-list-button {
        flex-shrink: 0;
      }
    }
  }
</style>
